import * as VW from "voximplant-websdk"

import { Client } from "modules/Call/sdk"
import { OWNER } from "modules/Call/common/constants"
import { createVoximplantLogSender } from "modules/Call/common/api"
import { IS_PRODUCTION } from "utils/env-management"

export type VoximplantLogger = {
    enable: () => void
    disable: () => void
}

const LOG_DURATION = 15 * 60 * 1000
const LOG_INTERVAL = 10 * 1000

let logger: VoximplantLogger

const setLogLevel = (sdk: Client, logLevel: VW.LogLevel) => {
    Object.values(VW.LogCategory).forEach((category) => {
        sdk.setLogLevel(category as VW.LogCategory, logLevel)
    })
}

const sendLog = (sdk: Client, owner: OWNER, bookingHash: string) => {
    if (!IS_PRODUCTION) console.log("logger: sending")

    const logs = sdk.getSilentLog()
    if (logs.length == 0) return

    void createVoximplantLogSender(owner, bookingHash)(logs.join("\n"))
    sdk.clearSilentLog()
}

const createLogger = ({
    bookingHash,
    owner,
    sdk,
}: {
    bookingHash: string
    owner: OWNER
    sdk: Client
}) => {
    let sendLogTimer: ReturnType<typeof setInterval>
    let disableLoggingTimer: ReturnType<typeof setTimeout>

    const enable = () => {
        if (sdk.alreadyInitialized) return

        if (!IS_PRODUCTION) console.log("logger: enabling")

        sdk.enableSilentLogging(true)
        setLogLevel(sdk, VW.LogLevel.INFO)

        sendLogTimer = setInterval(() => sendLog(sdk, owner, bookingHash), LOG_INTERVAL)
        disableLoggingTimer = setTimeout(() => disable(), LOG_DURATION)

        window.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") {
                sendLog(sdk, owner, bookingHash)
            }
        })
    }

    const disable = () => {
        if (!IS_PRODUCTION) console.log("logger: disabling")

        sendLog(sdk, owner, bookingHash)

        sdk.enableSilentLogging(false)
        setLogLevel(sdk, VW.LogLevel.WARNING)

        clearInterval(sendLogTimer)
        clearTimeout(disableLoggingTimer)
    }

    return {
        enable,
        disable,
    }
}

export const getLogger = (initial?: { bookingHash: string; owner: OWNER; sdk: Client }) => {
    if (!logger) {
        if (!initial) throw Error("logger: pass initial value")

        if (!IS_PRODUCTION) console.log("logger: creating")

        logger = createLogger(initial)
        return logger
    }

    if (!IS_PRODUCTION) console.log("logger: getting")

    return logger
}

export function logSdkEvent(message: string) {
    const timestamp = new Date().toISOString()
    const sdk = VW.getInstance()
    const fullMessage = `[AppLog] ${timestamp} ${message}`

    if (!IS_PRODUCTION) console.log(fullMessage)

    try {
        sdk.getSilentLog().push(fullMessage)
    } catch (err) {
        console.warn("Silent log push failed", err)
    }
}
